import React, { useEffect, useState, FunctionComponent, ReactElement } from 'react';
import i18next from 'i18next';
import { NavigateFunction, useNavigate } from 'react-router';
import { Alert } from 'react-bootstrap';
import { useAuthFetchFromRestAPI } from '../../../util/api';
import LoadingSpinner from '../../../components/LoadingSpinner';
import SecondaryButton from '../../../components/SecondaryButton';
import { formatDate } from '../../../util/date-utils';
import { TrainingPlanInfoDto } from '../../../../../shared/dtos/trainingplan.dto';
import { TrainingPlanStatus } from '../../../../../shared/utils/enums';

type TrainingPlanDetailsProps = {
  therapyId: string;
};

const TrainingPlanDetails: FunctionComponent<TrainingPlanDetailsProps> = ({ therapyId }: TrainingPlanDetailsProps): ReactElement => {
  const { authFetch } = useAuthFetchFromRestAPI();
  const navigate: NavigateFunction = useNavigate();

  const [trainingPlanInfo, setTrainingPlanInfo] = useState<TrainingPlanInfoDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const trainingPlanDetailUrl: string = trainingPlanInfo
    ? `/therapien/${therapyId}/trainingPlan/${trainingPlanInfo.id}`
    : `/therapien/${therapyId}/trainingPlan`;

  useEffect(() => {
    const fetchTrainingPlan = async (): Promise<void> => {
      try {
        const trainingPlanInfo: TrainingPlanInfoDto = await authFetch(`/api/v1/therapies/${therapyId}/training-plan`);

        setTrainingPlanInfo(trainingPlanInfo);
      } catch (error) {
        console.error('Error occured while fetching training plan', error);
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    };

    void fetchTrainingPlan();
  }, [therapyId, authFetch]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (hasError) {
    return <Alert variant="danger">{i18next.t('generic.error')}</Alert>;
  }

  const isPublished: boolean = trainingPlanInfo?.status === TrainingPlanStatus.PUBLISHED;
  const badgeClass: string = isPublished ? 'badge-bg-success' : 'badge-bg-warning';

  const dateRange: string | null = trainingPlanInfo?.startDate
    ? `${formatDate(trainingPlanInfo.startDate)}${trainingPlanInfo.endDate ? ` – ${formatDate(trainingPlanInfo.endDate)}` : ''}`
    : null;

  return (
    <div className="d-flex flex-wrap align-items-center gap-3">
      <div className="d-flex flex-column gap-2">
        {trainingPlanInfo ? (
          <span className={`badge ${badgeClass} fs-6 px-3 py-2 align-self-start`}>
            {i18next.t(`trainingPlan.status.${trainingPlanInfo.status}`)}
          </span>
        ) : (
          <span className="text-muted fs-6">{i18next.t('workouts.createTraining')}</span>
        )}

        {dateRange && (
          <span className="text-muted">
            <i className="far fa-calendar me-2" aria-hidden="true" />
            {dateRange}
          </span>
        )}
      </div>

      <div className="ms-auto">
        <SecondaryButton onClick={() => navigate(trainingPlanDetailUrl)}>
          {i18next.t(trainingPlanInfo ? 'workouts.editTraining' : 'workouts.createTraining')}
        </SecondaryButton>
      </div>
    </div>
  );
};

export default TrainingPlanDetails;
